// src/pages/SponsorLinksPage.js — link sponsorship invoices to their event + level
// [marker: fixes-oct21 v1]
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const money = (v) => `$${parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = (s) => (s ? new Date(`${String(s).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

export default function SponsorLinksPage() {
  const { isManagerOrAbove } = useAuth();
  const canEdit = isManagerOrAbove();
  const [data, setData] = useState(null);
  const [pick, setPick] = useState({}); // lineId -> tierId
  const [busy, setBusy] = useState(null);
  const load = () => api.get('/sponsor-links').then(r => {
    setData(r.data);
    const p = {}; (r.data.lines || []).forEach(l => { if (l.suggested_tier_id) p[l.line_id] = l.suggested_tier_id; });
    setPick(p);
  }).catch(() => toast.error('Could not load the list.'));
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const m = new Map();
    (data?.tiers || []).forEach(t => {
      if (!m.has(t.event_id)) m.set(t.event_id, { label: `${t.event_name} · ${fmtDay(t.event_date)}`, items: [] });
      m.get(t.event_id).items.push(t);
    });
    return Array.from(m.values());
  }, [data]);

  if (!data) return <div className="loading-page"><div className="spinner" /></div>;
  const ready = data.lines.filter(l => pick[l.line_id]);

  const linkOne = async (l) => {
    if (!pick[l.line_id]) return toast.error('Pick the event and sponsorship level first.');
    setBusy(l.line_id);
    try { await api.post('/sponsor-links/link', { lineId: l.line_id, tierId: pick[l.line_id] }); toast.success(`${l.customer} is now a sponsor of that event.`); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not link it.'); }
    finally { setBusy(null); }
  };
  const linkAll = async () => {
    if (!window.confirm(`Link ${ready.length} invoice line${ready.length === 1 ? '' : 's'} to the event and level picked for each?`)) return;
    setBusy('all');
    try {
      const r = await api.post('/sponsor-links/link-many', { links: ready.map(l => ({ lineId: l.line_id, tierId: pick[l.line_id] })) });
      toast.success(`Linked ${r.data.linked}.`); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not link them.'); }
    finally { setBusy(null); }
  };
  const dismiss = async (l) => {
    setBusy(l.line_id);
    try { await api.post('/sponsor-links/dismiss', { lineId: l.line_id }); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Link Sponsorships to Events</h1>
          <p className="page-subtitle">
            These invoices look like sponsorships but don’t say which event and level they’re for, so they don’t show as sponsors on the event,
            the member page, the Item List or the reports. Pick the event and level for each and press Link — the sponsor appears everywhere right away.
          </p>
        </div>
        {canEdit && ready.length > 0 && <button className="btn btn-primary" disabled={busy === 'all'} onClick={linkAll}>{busy === 'all' ? 'Linking…' : `Link all ${ready.length} picked`}</button>}
      </div>

      {data.lines.length === 0 ? (
        <div className="card empty-state" style={{ padding: 30 }}><div className="empty-state-icon">✅</div><h3>Every sponsorship invoice is linked to its event.</h3></div>
      ) : (
        <div className="card">
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Invoice</th><th>Customer</th><th>Line</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ minWidth: 280 }}>Event &amp; sponsorship level</th><th></th></tr></thead>
              <tbody>
                {data.lines.map(l => (
                  <tr key={l.line_id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <Link to={`/invoices/${l.invoice_id}`}>{l.invoice_number}</Link>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{fmtDay(l.issue_date)} · {l.status}</div>
                    </td>
                    <td>{l.customer || '—'}</td>
                    <td>{l.name}{l.description ? <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{l.description}</div> : null}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{money(l.line_total)}</td>
                    <td>
                      <select className="form-select" value={pick[l.line_id] || ''} disabled={!canEdit}
                              style={{ background: pick[l.line_id] ? (l.suggested_tier_id === pick[l.line_id] ? '#f0fdf4' : 'white') : '#fffbeb' }}
                              onChange={e => setPick(p => ({ ...p, [l.line_id]: e.target.value ? parseInt(e.target.value, 10) : null }))}>
                        <option value="">— Pick event &amp; level —</option>
                        {groups.map(g => (
                          <optgroup key={g.label} label={g.label}>
                            {g.items.map(t => <option key={t.id} value={t.id}>{t.name} ({money(t.price)})</option>)}
                          </optgroup>
                        ))}
                      </select>
                      {l.suggested_tier_id && l.suggested_tier_id === pick[l.line_id] && <div style={{ fontSize: '0.72rem', color: 'var(--success)' }}>Best guess — check it</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {canEdit && <>
                        <button className="btn btn-primary btn-sm" disabled={busy === l.line_id || !pick[l.line_id]} onClick={() => linkOne(l)}>Link</button>{' '}
                        <button className="btn btn-ghost btn-sm" disabled={busy === l.line_id} onClick={() => dismiss(l)} title="Hide it from this list">Not a sponsorship</button>
                      </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className="form-hint" style={{ marginTop: 10 }}>
        Don’t see the right level? Add it on the event’s Sponsorships tab first, then come back.
        New invoices ask for the event and level as you create them, so this list should stay empty from now on.
      </p>
    </div>
  );
}
